import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import UWGuideBody, { useGuideSections, useGuideDocument } from '../../../components/UWGuideContent';

export default function InvestorUWGuide() {
  // The table of contents comes with the served guide (GET /api/uw-matrix/guide — authorized viewers only).
  const sections = useGuideSections();
  // The edition, policy version and effective date come with the served document — never a typed date.
  const { doc } = useGuideDocument();
  const [search, setSearch] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => { document.title = 'DoorlyMarket | AFM Underwriting Guidelines'; }, []);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, []);

  const highlightSearch = useMemo(() => () => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll('mark[data-uw-search]').forEach((m) => {
      const parent = m.parentNode;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
      parent.normalize();
    });
    if (!search.trim()) { setMatchCount(0); return; }
    const term = search.trim();
    const re = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    let count = 0;
    let firstMatch = null;
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        if (!text || !re.test(text)) return;
        re.lastIndex = 0;
        const frag = document.createDocumentFragment();
        let lastIdx = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
          if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
          const mark = document.createElement('mark');
          mark.setAttribute('data-uw-search', '1');
          mark.style.background = '#ffdd86';
          mark.style.color = '#111111';
          mark.style.padding = '0 2px';
          mark.style.borderRadius = '3px';
          mark.appendChild(document.createTextNode(m[0]));
          frag.appendChild(mark);
          lastIdx = m.index + m[0].length;
          count++;
          if (!firstMatch) firstMatch = mark;
        }
        if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (['SCRIPT','STYLE','MARK','INPUT','BUTTON','TEXTAREA'].includes(node.tagName)) return;
        Array.from(node.childNodes).forEach(walk);
      }
    };
    walk(root);
    setMatchCount(count);
    if (firstMatch) firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [search]);

  useEffect(() => {
    const t = setTimeout(highlightSearch, 80);
    return () => clearTimeout(t);
  }, [highlightSearch]);

  return (
    <InvestorPortalLayout>
      <style>{`.uw-section:hover .uw-copy-link { opacity: 1 !important; }`}</style>

      <div style={{ marginBottom: 12, fontSize: 13 }}>
        <Link to="/market/portal/resources" style={{ color: '#3a6ad9', textDecoration: 'none', fontWeight: 600 }}>
          ← Back to Resources
        </Link>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 28px', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111111' }}>
          Doorly AFM<sup style={{ fontSize: '0.55em', verticalAlign: 'super' }}>™</sup> Underwriting Guidelines
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
          Reference document for DoorlyMarket investors
          {doc ? <span data-testid="uw-guide-edition"> · Version {doc.edition} · policy {doc.version}, effective {doc.effective}</span> : null}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        <aside style={{ width: 220, flexShrink: 0, position: 'sticky', top: 84, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', display: 'none' }}
          className="uw-toc-desktop">
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 10, paddingLeft: 6 }}>
              Contents
            </div>
            {sections.map((s, i) => (
              <a key={s.id} href={`#${s.id}`}
                style={{ display: 'block', padding: '6px 8px', borderRadius: 6, fontSize: 12.5, color: '#374151', textDecoration: 'none', lineHeight: 1.4 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                <span style={{ color: '#9ca3af', fontVariantNumeric: 'tabular-nums', marginRight: 8 }}>{String(i + 1).padStart(2, '0')}</span>
                {s.label}
              </a>
            ))}
          </div>
        </aside>
        <style>{`@media (min-width: 900px) { .uw-toc-desktop { display: block !important; } }`}</style>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ position: 'sticky', top: 64, zIndex: 5, background: '#f9fafb', paddingBottom: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 10px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input type="text" placeholder="Search guidelines…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#111827', height: 32 }} />
              {search && (
                <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {matchCount === 0 ? 'No matches' : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
                </span>
              )}
              {search && (
                <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16 }}>✕</button>
              )}
            </div>
          </div>

          <div ref={contentRef} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '24px 32px' }}>
            <UWGuideBody allowCopyLinks={false} />
          </div>
        </div>
      </div>
    </InvestorPortalLayout>
  );
}
